import { useEffect, useMemo, useRef, useState } from "react";
import type { Focused } from "react-credit-cards-2";

import {
  AnimatePresence,
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";

import {
  detectCardBrand,
  formatCardNumber,
} from "js/components/Payments/cardUtils";

export type { Focused };

interface CreditCard3DProps {
  number: string;
  name: string;
  expiry: string;
  cvc: string;
  focused: Focused;
}

const BRAND_LABELS: Record<string, string> = {
  visa: "VISA",
  mastercard: "MASTERCARD",
  "american-express": "AMEX",
  discover: "DISCOVER",
  diners: "DINERS",
  jcb: "JCB",
  unionpay: "UNIONPAY",
};

function ChipSvg() {
  return (
    <svg
      width="40"
      height="30"
      viewBox="0 0 36 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="drop-shadow-md"
    >
      <rect
        x="0.5"
        y="0.5"
        width="35"
        height="27"
        rx="3.5"
        fill="url(#chip3dGrad)"
        stroke="#b8860b"
        strokeWidth="0.5"
      />
      <line
        x1="0"
        y1="10"
        x2="36"
        y2="10"
        stroke="#b8860b"
        strokeWidth="0.5"
        opacity="0.5"
      />
      <line
        x1="0"
        y1="18"
        x2="36"
        y2="18"
        stroke="#b8860b"
        strokeWidth="0.5"
        opacity="0.5"
      />
      <line
        x1="12"
        y1="0"
        x2="12"
        y2="28"
        stroke="#b8860b"
        strokeWidth="0.5"
        opacity="0.4"
      />
      <line
        x1="24"
        y1="0"
        x2="24"
        y2="28"
        stroke="#b8860b"
        strokeWidth="0.5"
        opacity="0.4"
      />
      <defs>
        <linearGradient
          id="chip3dGrad"
          x1="0"
          y1="0"
          x2="36"
          y2="28"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#daa520" />
          <stop offset="0.5" stopColor="#f0d060" />
          <stop offset="1" stopColor="#b8860b" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function ContactlessSvg() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      className="opacity-40"
    >
      <path
        d="M8.5 16.5c-1.6-1.6-2.5-3.8-2.5-6s.9-4.4 2.5-6"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M12 14c-.8-.8-1.3-1.9-1.3-3s.5-2.2 1.3-3"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M15.5 11.5c-.4-.4-.6-.9-.6-1.5s.2-1.1.6-1.5"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function formatDisplayNumber(number: string): string {
  if (!number || number.replace(/\D/g, "").length === 0) {
    return "••••  ••••  ••••  ••••";
  }
  const formatted = formatCardNumber(number);
  const groups = formatted.split(" ");
  return groups.map((g) => g.padEnd(4, "•")).join("  ");
}

function CreditCard3D({
  number,
  name,
  expiry,
  cvc,
  focused,
}: CreditCard3DProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const isFlipped = focused === "cvc";

  const [prefersReduced, setPrefersReduced] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReduced(mql.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReduced(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  const brand = useMemo(() => detectCardBrand(number), [number]);
  const brandLabel = BRAND_LABELS[brand] ?? "";
  const displayNumber = useMemo(() => formatDisplayNumber(number), [number]);
  const displayName = name.trim() || "TITULAR";
  const displayExpiry = useMemo(() => {
    const digits = expiry.replace(/\D/g, "");
    if (digits.length === 0) return "MM/AA";
    // Already formatted as MM/YY
    if (expiry.includes("/")) return expiry;
    // Raw digits: format as MM/YY
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
  }, [expiry]);

  const rawRotateX = useMotionValue(0);
  const rawRotateY = useMotionValue(0);
  const rotateX = useSpring(rawRotateX, { stiffness: 150, damping: 15 });
  const rotateY = useSpring(rawRotateY, { stiffness: 150, damping: 15 });

  const flipTarget = useMotionValue(isFlipped ? 180 : 0);
  const flipSpring = useSpring(flipTarget, { stiffness: 300, damping: 30 });
  useEffect(() => {
    flipTarget.set(isFlipped ? 180 : 0);
  }, [isFlipped, flipTarget]);

  const combinedRotateY = useTransform(
    [flipSpring, rotateY],
    ([f, p]) => (f as number) + (p as number),
  );

  const shimmerX = useTransform(rotateY, [-15, 15], [-30, 30]);
  const shimmerY = useTransform(rotateX, [-15, 15], [-20, 20]);

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (prefersReduced) return;
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const normalizedX = (e.clientX - rect.left) / rect.width - 0.5;
    const normalizedY = (e.clientY - rect.top) / rect.height - 0.5;
    rawRotateY.set(normalizedX * 30);
    rawRotateX.set(-normalizedY * 30);
  }

  function handleMouseLeave() {
    rawRotateX.set(0);
    rawRotateY.set(0);
  }

  return (
    <div className="w-full max-w-[300px] sm:max-w-[360px] mx-auto select-none">
      <div
        ref={cardRef}
        style={{ perspective: "1000px" }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <motion.div
          style={{
            transformStyle: "preserve-3d",
            rotateX: prefersReduced ? 0 : rotateX,
            rotateY: prefersReduced ? (isFlipped ? 180 : 0) : combinedRotateY,
          }}
          transition={
            prefersReduced
              ? { duration: 0 }
              : { duration: 0.6, ease: [0.23, 1, 0.32, 1] }
          }
          className="relative aspect-[1.586/1] w-full"
        >
          <div
            style={{ backfaceVisibility: "hidden" }}
            className="absolute inset-0 rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 p-4 sm:p-6 flex flex-col justify-between overflow-hidden shadow-2xl shadow-black/40"
          >
            <div className="absolute inset-0 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl pointer-events-none" />

            <motion.div
              className="absolute inset-0 pointer-events-none rounded-2xl opacity-[0.07]"
              style={{
                x: shimmerX,
                y: shimmerY,
                background:
                  "radial-gradient(ellipse 120% 80% at 50% 50%, rgba(255,255,255,0.4), transparent 60%)",
              }}
            />

            <div
              className="absolute inset-0 pointer-events-none rounded-2xl opacity-[0.03]"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
              }}
            />

            <div className="flex items-start justify-between relative z-10">
              <div className="flex items-center gap-2.5">
                <ChipSvg />
                <ContactlessSvg />
              </div>
              <AnimatePresence mode="wait">
                {brandLabel && (
                  <motion.span
                    key={brandLabel}
                    initial={{ opacity: 0, x: 8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -8 }}
                    transition={{ duration: 0.25 }}
                    className="text-white/80 text-sm font-bold tracking-[0.2em]"
                  >
                    {brandLabel}
                  </motion.span>
                )}
              </AnimatePresence>
            </div>

            <div className="relative z-10">
              <p className="text-white text-[0.95rem] sm:text-[1.1rem] tracking-[0.2em] font-mono leading-none drop-shadow-sm">
                {displayNumber}
              </p>
            </div>

            <div className="flex items-end justify-between relative z-10">
              <div className="flex flex-col gap-0.5 min-w-0 flex-1 mr-4">
                <span className="text-[0.5rem] text-white/30 uppercase tracking-[0.15em]">
                  Titular
                </span>
                <p className="text-white/85 text-xs font-medium tracking-wider uppercase truncate">
                  {displayName}
                </p>
              </div>
              <div className="flex flex-col items-end gap-0.5 shrink-0">
                <span className="text-[0.5rem] text-white/30 uppercase tracking-[0.15em]">
                  Vence
                </span>
                <p className="text-white/85 text-xs font-medium tracking-wider font-mono">
                  {displayExpiry}
                </p>
              </div>
            </div>
          </div>

          <div
            style={{
              backfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
            }}
            className="absolute inset-0 rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 flex flex-col overflow-hidden shadow-2xl shadow-black/40"
          >
            <div className="absolute inset-0 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl pointer-events-none" />

            <div className="w-full h-11 bg-black/80 mt-6" />

            <div className="flex items-center gap-3 mx-6 mt-5 relative z-10">
              <div className="flex-1 h-9 bg-white/90 rounded flex items-center justify-end px-4">
                <span className="text-slate-800 text-sm font-mono tracking-[0.2em] font-semibold">
                  {focused === "cvc" ? cvc || "•••" : "•••"}
                </span>
              </div>
              <span className="text-[0.6rem] text-white/40 uppercase tracking-wider font-medium">
                CVV
              </span>
            </div>

            <div className="flex-1 flex items-end justify-between px-6 pb-5 relative z-10">
              <p className="text-[0.45rem] text-white/20 leading-tight max-w-[55%]">
                Esta tarjeta es propiedad del banco emisor. Su uso no autorizado
                puede ser penalizado conforme a la ley.
              </p>
              {brandLabel && (
                <span className="text-white/30 text-xs font-bold tracking-[0.2em]">
                  {brandLabel}
                </span>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default CreditCard3D;
